import React, { useState, useMemo } from "react";
import {
    Building2, User, AlertTriangle, TrendingUp, TrendingDown, ShieldCheck, Scale,
    Info, ArrowRight, Calculator, Sparkles, FileWarning
} from "lucide-react";
import { Link } from "react-router-dom";
import ContactSection from "./ContactSection";

const PREFILLED_OGGETTO = "Simulazione licenziamento — richiesta parere legale";
const PREFILLED_MESSAGGIO =
    "Buongiorno, ho effettuato una simulazione per un licenziamento e vorrei un parere legale.";

// ---------------------------------------------------------------------------
// Pure calculation
// ---------------------------------------------------------------------------
// Sources: L.604/1966, art.18 St.Lav. (post L.92/2012), D.Lgs.23/2015 (Jobs Act),
// L.183/2010, D.M. 55/2014 (parametri forensi), Delibera INPS ticket licenziamento.
// I risultati sono ORIENTATIVI e a scopo divulgativo. Solo un giuslavorista può
// fornire una valutazione vincolante.

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const eur = (n) =>
    (n || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

function computeAssessment({ hireDate, size, seniority, salary, dismissalType }) {
    const s = Math.max(1, Math.min(35, Number(seniority) || 1));
    const R = Math.max(0, Number(salary) || 0);

    const isPost2015 = hireDate === "post_2015";
    const isBigCompany = size === "gt15";
    const isDiscriminatory = dismissalType === "discriminatorio";

    // ------ Metrica 1: mensilità stimate ------
    let monthsMin, monthsMax, regimeLabel, hasReintegra, reintegraLabel;
    if (isDiscriminatory) {
        // Tutela reale piena (art. 18 c.1 + art. 2 D.Lgs. 23/2015): reintegra obbligatoria + minimo 5 mensilità
        monthsMin = 5;
        monthsMax = Math.max(5, Math.min(24, s * 1.5));
        regimeLabel = "Tutela Reale Piena — reintegra obbligatoria + risarcimento";
        hasReintegra = true;
        reintegraLabel = "Reintegra obbligatoria + risarcimento minimo 5 mensilità (max 12 come sostitutiva)";
    } else if (isPost2015) {
        if (isBigCompany) {
            // D.Lgs. 23/2015 (Jobs Act — tutele crescenti) - come modificato Corte Cost. 194/2018 e L.96/2018
            monthsMin = 6;
            monthsMax = clamp(2 * s, 6, 36);
            regimeLabel = "Tutele crescenti (Jobs Act) — indennità 6–36 mensilità";
            hasReintegra = dismissalType === "disciplinare"; // rischio reintegra solo se fatto contestato insussistente
            reintegraLabel = hasReintegra
                ? "Rischio reintegra se fatto materiale contestato risulta insussistente (art.3 c.2 D.Lgs.23/2015)"
                : null;
        } else {
            // Piccole imprese post-2015: indennità dimezzata (art.9 D.Lgs.23/2015)
            monthsMin = 3;
            monthsMax = clamp(s, 3, 6);
            regimeLabel = "Piccole imprese (Jobs Act) — indennità 3–6 mensilità";
            hasReintegra = false;
            reintegraLabel = null;
        }
    } else {
        if (isBigCompany) {
            // Art.18 St.Lav. riformato (L.92/2012 Fornero)
            monthsMin = 12;
            monthsMax = 24;
            regimeLabel = "Art. 18 St.Lav. (Legge Fornero) — indennità 12–24 mensilità";
            hasReintegra = dismissalType === "disciplinare" || dismissalType === "oggettivo";
            reintegraLabel = "Rischio reintegra (tutela reale attenuata) se manifesta insussistenza del fatto";
        } else {
            // Piccole imprese ante-2015: L.108/1990
            monthsMin = 2.5;
            monthsMax = 6;
            regimeLabel = "Piccole imprese (L. 108/1990) — indennità 2,5–6 mensilità";
            hasReintegra = false;
            reintegraLabel = null;
        }
    }

    const riskMinEur = monthsMin * R;
    const riskMaxEur = monthsMax * R;

    // ------ Metrica 2: costi accessori azienda ------
    // Ticket licenziamento INPS 2026 (~41% NASpI max × durata max; stimato ~584€/anno anzianità, cap 3 anni)
    // Semplificato: ~1.800 € base + bonus anzianità
    const ticketInps = Math.min(3, s) * 584 + 200; // ~1800€ tipico
    // Spese legali doppio grado (parametri D.M. 55/2014, scaglione medio 26k-52k)
    const legalFees = clamp(R * 0.15 + 3500, 3500, 12000);
    const accessoryCosts = ticketInps + legalFees;

    // ------ Metrica 3: target transazione (Azienda) / valore vertenza (Lavoratore) ------
    const settlementMinEur = riskMaxEur * 0.4;
    const settlementMaxEur = riskMaxEur * 0.6;
    // Buonuscita worker: tipicamente 50-75% del massimo teorico, ma esentasse fino a 1 mensilità/anno
    const workerNegotiationMin = riskMaxEur * 0.5;
    const workerNegotiationMax = riskMaxEur * 0.75;

    // ------ Alert level ------
    let alertLevel; // "low" | "medium" | "high"
    if (isDiscriminatory || hasReintegra) alertLevel = "high";
    else if (riskMaxEur >= 40000) alertLevel = "high";
    else if (riskMaxEur >= 12000) alertLevel = "medium";
    else alertLevel = "low";

    // ------ Reintegra sostitutiva (15 mensilità) — solo se applicabile ------
    const substituteAllowanceEur = hasReintegra ? 15 * R : 0;

    return {
        R,
        s,
        monthsMin,
        monthsMax,
        regimeLabel,
        hasReintegra,
        reintegraLabel,
        isDiscriminatory,
        riskMinEur,
        riskMaxEur,
        ticketInps,
        legalFees,
        accessoryCosts,
        settlementMinEur,
        settlementMaxEur,
        workerNegotiationMin,
        workerNegotiationMax,
        alertLevel,
        substituteAllowanceEur,
    };
}

// ---------------------------------------------------------------------------
// UI helpers
// ---------------------------------------------------------------------------
const alertPalette = {
    low: {
        bg: "bg-emerald-50",
        border: "border-emerald-500",
        text: "text-emerald-800",
        icon: ShieldCheck,
        label: "RISCHIO BASSO",
    },
    medium: {
        bg: "bg-amber-50",
        border: "border-amber-500",
        text: "text-amber-800",
        icon: AlertTriangle,
        label: "RISCHIO MEDIO",
    },
    high: {
        bg: "bg-red-50",
        border: "border-red-500",
        text: "text-red-800",
        icon: FileWarning,
        label: "RISCHIO ALTO",
    },
};

const MetricCard = ({ title, subtitle, value, hint, accent, testId, Icon }) => (
    <div
        data-testid={testId}
        className={`bg-white border-l-4 ${accent} p-5 lg:p-6 shadow-sm`}
    >
        <div className="flex items-start justify-between gap-3 mb-3">
            <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{title}</div>
                {subtitle && <div className="text-xs text-slate-400 mt-1">{subtitle}</div>}
            </div>
            {Icon && <Icon size={18} className="text-slate-400 flex-shrink-0" />}
        </div>
        <div className="font-serif text-2xl md:text-3xl text-navy leading-tight break-words">{value}</div>
        {hint && <div className="text-xs text-slate-500 mt-3 leading-relaxed">{hint}</div>}
    </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function DismissalRiskAssessment() {
    const [profile, setProfile] = useState("worker"); // "worker" | "company"
    const [hireDate, setHireDate] = useState("post_2015");
    const [size, setSize] = useState("gt15");
    const [seniority, setSeniority] = useState(5);
    const [salary, setSalary] = useState(2500);
    const [dismissalType, setDismissalType] = useState("oggettivo");

    const result = useMemo(
        () => computeAssessment({ hireDate, size, seniority, salary, dismissalType }),
        [hireDate, size, seniority, salary, dismissalType],
    );

    const isCompany = profile === "company";
    const alert = alertPalette[result.alertLevel];
    const AlertIcon = alert.icon;

    // Accent color per profile
    const profileAccent = isCompany
        ? { chip: "bg-red-50 text-red-800 border-red-300", pill: "bg-red-600", label: "Prospettiva Azienda" }
        : { chip: "bg-emerald-50 text-emerald-800 border-emerald-300", pill: "bg-emerald-600", label: "Prospettiva Lavoratore" };

    return (
        <section
            id="rischio-licenziamento"
            data-testid="risk-assessment-section"
            className="py-24 lg:py-32 bg-[#FAFAFA] relative"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
                {/* HEADER */}
                <div className="max-w-3xl mb-12">
                    <div className="flex items-center gap-4 mb-6">
                        <span className="gold-rule" />
                        <span className="label-eyebrow">Strumento gratuito</span>
                    </div>
                    <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] mb-6">
                        Valutatore <span className="italic text-gold-deep">rischio economico</span> da licenziamento
                    </h2>
                    <p className="text-slate-600 text-base md:text-lg leading-relaxed">
                        Stima orientativa dell'esposizione economica di un licenziamento — dal punto di vista
                        dell'<strong className="text-navy">azienda</strong> (rischio) o del{" "}
                        <strong className="text-navy">lavoratore</strong> (opportunità), secondo Jobs Act, Legge Fornero
                        e art. 18 St.Lav.
                    </p>
                </div>

                {/* PROFILE TOGGLE */}
                <div className="mb-10">
                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-4">Chi sei?</div>
                    <div className="inline-flex flex-col sm:flex-row gap-3 sm:gap-0 sm:bg-white sm:border sm:border-slate-300 sm:p-1 w-full sm:w-auto">
                        <button
                            type="button"
                            data-testid="profile-worker"
                            onClick={() => setProfile("worker")}
                            className={`inline-flex items-center gap-3 px-5 sm:px-7 py-3 text-sm font-semibold tracking-wide transition-all ${
                                !isCompany
                                    ? "bg-navy text-white shadow-md"
                                    : "bg-white sm:bg-transparent border border-slate-300 sm:border-0 text-slate-500 hover:text-navy"
                            }`}
                        >
                            <User size={16} />
                            <span className="text-left leading-tight">
                                Sono un Lavoratore / Dirigente
                                <span className="block text-[11px] font-normal opacity-70 mt-0.5">
                                    Focus: valore vertenza e diritti
                                </span>
                            </span>
                        </button>
                        <button
                            type="button"
                            data-testid="profile-company"
                            onClick={() => setProfile("company")}
                            className={`inline-flex items-center gap-3 px-5 sm:px-7 py-3 text-sm font-semibold tracking-wide transition-all ${
                                isCompany
                                    ? "bg-navy text-white shadow-md"
                                    : "bg-white sm:bg-transparent border border-slate-300 sm:border-0 text-slate-500 hover:text-navy"
                            }`}
                        >
                            <Building2 size={16} />
                            <span className="text-left leading-tight">
                                Sono un'Azienda
                                <span className="block text-[11px] font-normal opacity-70 mt-0.5">
                                    Focus: prevenzione rischi e costi
                                </span>
                            </span>
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                    {/* -------- FORM -------- */}
                    <div className="lg:col-span-5">
                        <div className="bg-white border border-slate-200 p-6 lg:p-8">
                            <div className="flex items-center gap-2 mb-6">
                                <Calculator size={16} className="text-gold-deep" />
                                <h3 className="font-serif text-navy text-xl">Parametri di calcolo</h3>
                            </div>

                            {/* 1. Data assunzione */}
                            <FieldGroup label="Data di assunzione">
                                <RadioBar
                                    name="hireDate"
                                    value={hireDate}
                                    onChange={setHireDate}
                                    options={[
                                        { v: "post_2015", label: "Dopo il 7 Marzo 2015", sub: "(Jobs Act)" },
                                        { v: "pre_2015", label: "Prima del 7 Marzo 2015", sub: "(Art. 18)" },
                                    ]}
                                    testIdPrefix="hire"
                                />
                            </FieldGroup>

                            {/* 2. Dimensioni aziendali */}
                            <FieldGroup label="Dimensioni aziendali">
                                <RadioBar
                                    name="size"
                                    value={size}
                                    onChange={setSize}
                                    options={[
                                        { v: "gt15", label: "Più di 15 dipendenti", sub: "(tutela forte)" },
                                        { v: "lte15", label: "Fino a 15 dipendenti", sub: "(piccole imprese)" },
                                    ]}
                                    testIdPrefix="size"
                                />
                            </FieldGroup>

                            {/* 3. Anzianità */}
                            <FieldGroup label={`Anzianità di servizio: ${result.s} ${result.s === 1 ? "anno" : "anni"}`}>
                                <input
                                    type="range"
                                    min="1"
                                    max="35"
                                    step="1"
                                    value={seniority}
                                    onChange={(e) => setSeniority(Number(e.target.value))}
                                    data-testid="input-seniority"
                                    className="w-full accent-[#0B132B]"
                                />
                                <div className="flex justify-between text-[11px] text-slate-400 mt-1">
                                    <span>1</span><span>10</span><span>20</span><span>30</span><span>35</span>
                                </div>
                            </FieldGroup>

                            {/* 4. Retribuzione */}
                            <FieldGroup label="Retribuzione lorda mensile (RAL/12)">
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-serif">€</span>
                                    <input
                                        type="number"
                                        min="800"
                                        step="50"
                                        value={salary}
                                        onChange={(e) => setSalary(e.target.value)}
                                        data-testid="input-salary"
                                        className="w-full border border-slate-300 pl-9 pr-4 py-3 text-navy font-medium focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold transition-colors"
                                    />
                                </div>
                            </FieldGroup>

                            {/* 5. Tipo licenziamento */}
                            <FieldGroup label="Tipo di licenziamento">
                                <select
                                    value={dismissalType}
                                    onChange={(e) => setDismissalType(e.target.value)}
                                    data-testid="input-dismissal-type"
                                    className="w-full border border-slate-300 px-4 py-3 text-navy font-medium focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold transition-colors bg-white"
                                >
                                    <option value="oggettivo">Motivo oggettivo (economico / riorganizzazione)</option>
                                    <option value="disciplinare">Motivo soggettivo / disciplinare</option>
                                    <option value="discriminatorio">Discriminatorio / ritorsivo / verbale</option>
                                </select>
                            </FieldGroup>

                            <div className="flex items-start gap-2 text-[11px] text-slate-500 leading-relaxed mt-4 pt-4 border-t border-slate-100">
                                <Info size={13} className="text-gold-deep mt-0.5 flex-shrink-0" />
                                <span>
                                    Stima orientativa a scopo divulgativo. La valutazione definitiva richiede l'analisi
                                    dei fatti concreti da parte di un avvocato giuslavorista.
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* -------- RESULTS -------- */}
                    <div className="lg:col-span-7">
                        {/* Profile chip + title */}
                        <div className="flex items-center gap-3 flex-wrap mb-5">
                            <span
                                data-testid="profile-chip"
                                className={`inline-flex items-center gap-2 px-3 py-1 border text-[11px] font-bold uppercase tracking-[0.18em] ${profileAccent.chip}`}
                            >
                                <span className={`w-1.5 h-1.5 ${profileAccent.pill}`} />
                                {profileAccent.label}
                            </span>
                            <span className="text-xs text-slate-400">{result.regimeLabel}</span>
                        </div>

                        <h3
                            data-testid="result-title"
                            className="font-serif text-navy text-2xl md:text-3xl mb-6 leading-tight"
                        >
                            {isCompany
                                ? "Valutazione del rischio aziendale"
                                : "Valore potenziale della tua pratica"}
                        </h3>

                        {/* Alert banner */}
                        <div
                            data-testid="alert-banner"
                            className={`border-l-4 ${alert.border} ${alert.bg} ${alert.text} px-5 py-4 mb-6 flex items-start gap-3`}
                        >
                            <AlertIcon size={20} className="flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-1">
                                    {isCompany ? alert.label : (result.alertLevel === "high" ? "VERTENZA DI ALTO VALORE" : result.alertLevel === "medium" ? "VERTENZA DI VALORE MEDIO" : "VERTENZA CONTENUTA")}
                                </div>
                                <div className="text-sm leading-relaxed">
                                    {result.isDiscriminatory
                                        ? (isCompany
                                            ? "Rischio massimo: reintegra obbligatoria e risarcimento cumulabile. Approccio conciliativo urgente."
                                            : "Tutela reale piena: hai diritto alla reintegra + risarcimento minimo 5 mensilità. Massima protezione legale.")
                                        : result.hasReintegra
                                            ? (isCompany
                                                ? "Attenzione: possibile rischio reintegra se emergerà insussistenza del fatto contestato."
                                                : "Possibilità di ottenere la reintegra nel posto di lavoro o l'indennità sostitutiva.")
                                            : result.alertLevel === "high"
                                                ? (isCompany
                                                    ? "Esposizione economica significativa. Un accordo transattivo ridurrebbe notevolmente il costo complessivo."
                                                    : "Vertenza di valore rilevante: alto potenziale sia in transazione che in giudizio.")
                                                : result.alertLevel === "medium"
                                                    ? (isCompany
                                                        ? "Esposizione media, gestibile in via transattiva con contenimento dei costi accessori."
                                                        : "Vertenza di valore medio, risolvibile con indennità economica di rilievo.")
                                                    : (isCompany
                                                        ? "Rischio contenuto principalmente entro il perimetro indennitario. Valuta transazione."
                                                        : "Vertenza risolvibile con indennità economica. Trattativa consigliata.")}
                                </div>
                            </div>
                        </div>

                        {/* METRICHE */}
                        {isCompany ? (
                            <CompanyMetrics r={result} />
                        ) : (
                            <WorkerMetrics r={result} />
                        )}

                        {/* N.B. — Variabili non considerate nel calcolo */}
                        <div
                            data-testid="risk-nb-disclaimer"
                            className="mt-6 border-l-4 border-gold bg-white p-5 lg:p-6 shadow-sm"
                        >
                            <div className="flex items-start gap-3">
                                <Info size={18} className="text-gold-deep flex-shrink-0 mt-0.5" />
                                <div className="text-sm leading-relaxed text-slate-700">
                                    <span className="font-bold text-navy uppercase tracking-wider text-[11px] mr-2">N.B.</span>
                                    Il calcolo <strong className="text-navy">non tiene conto di eventuali variabili</strong>
                                    {" "}— quali, ad esempio, il <em>danno a diverso titolo</em> cagionato o subito dal
                                    lavoratore per la condotta datoriale (danno psico-fisico, svilimento professionale,
                                    demansionamento, danno all&apos;immagine, danno esistenziale, ecc.) — che possono influire
                                    significativamente sulla quantificazione delle somme dovute al dipendente.
                                </div>
                            </div>
                        </div>

                        {/* CTA */}
                        <div className="mt-8 bg-navy text-white p-6 lg:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <Sparkles size={14} className="text-gold" />
                                    <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-gold">
                                        Consulenza personalizzata
                                    </span>
                                </div>
                                <div className="font-serif text-lg md:text-xl leading-tight">
                                    {isCompany
                                        ? "Prevenire il contenzioso costa il 60% in meno di subirlo."
                                        : "Ogni caso ha specificità che possono aumentare significativamente il valore."}
                                </div>
                            </div>
                            <Link
                                to="/#contatti"
                                data-testid="risk-cta"
                                className="btn-gold whitespace-nowrap group"
                            >
                                Prima consulenza gratuita
                                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </div>
                </div>

                {/* INLINE CONTACT FORM (pre-compilato per follow-up sulla simulazione) */}
                <div className="mt-16 pt-12 border-t border-slate-200" data-testid="risk-inline-contact">
                    <div className="max-w-3xl mb-8">
                        <div className="flex items-center gap-4 mb-4">
                            <span className="gold-rule" />
                            <span className="label-eyebrow">Richiedi un parere legale</span>
                        </div>
                        <h3 className="font-serif text-navy text-3xl md:text-4xl leading-tight mb-3">
                            Vuoi un&apos;analisi <span className="italic text-gold-deep">personalizzata</span> del tuo caso?
                        </h3>
                        <p className="text-slate-600 leading-relaxed">
                            La simulazione è orientativa. Compila il modulo per una valutazione precisa da parte
                            dell&apos;Avv. Marco Candido — <strong className="text-navy">prima consulenza gratuita</strong>.
                        </p>
                    </div>
                    <ContactSection
                        compact
                        sectionId="rischio-contatto"
                        testId="risk-contact-form-section"
                        heading="Richiesta parere legale"
                        defaultOggetto={PREFILLED_OGGETTO}
                        defaultMessaggio={PREFILLED_MESSAGGIO}
                    />
                </div>
            </div>
        </section>
    );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function FieldGroup({ label, children }) {
    return (
        <div className="mb-5 last:mb-0">
            <label className="block text-[11px] font-bold tracking-[0.18em] text-navy uppercase mb-3">{label}</label>
            {children}
        </div>
    );
}

function RadioBar({ name, value, onChange, options, testIdPrefix }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {options.map((o) => {
                const active = value === o.v;
                return (
                    <button
                        key={o.v}
                        type="button"
                        data-testid={`${testIdPrefix}-${o.v}`}
                        onClick={() => onChange(o.v)}
                        className={`text-left px-3 py-2.5 text-xs border transition-all ${
                            active
                                ? "bg-navy text-white border-navy"
                                : "bg-white text-slate-600 border-slate-300 hover:border-navy"
                        }`}
                    >
                        <div className="font-semibold leading-tight">{o.label}</div>
                        {o.sub && <div className={`text-[10px] mt-0.5 ${active ? "text-slate-300" : "text-slate-400"}`}>{o.sub}</div>}
                    </button>
                );
            })}
        </div>
    );
}

function CompanyMetrics({ r }) {
    return (
        <div className="grid grid-cols-1 gap-4">
            <MetricCard
                testId="metric-company-risk"
                title="Rischio risarcitorio in tribunale"
                subtitle={`${r.monthsMin.toLocaleString("it-IT")} – ${r.monthsMax.toLocaleString("it-IT")} mensilità × ${eur(r.R)}`}
                value={`${eur(r.riskMinEur)} – ${eur(r.riskMaxEur)}`}
                accent="border-red-500"
                Icon={TrendingUp}
                hint={r.reintegraLabel || "Indennità meramente risarcitoria (art. 3 D.Lgs. 23/2015 come modificato)"}
            />
            <MetricCard
                testId="metric-company-costs"
                title="Costi accessori stimati"
                subtitle="Ticket INPS + spese legali doppio grado (D.M. 55/2014)"
                value={eur(r.accessoryCosts)}
                accent="border-orange-500"
                Icon={FileWarning}
                hint={`Ticket licenziamento INPS ~${eur(r.ticketInps)} + spese legali stimate ~${eur(r.legalFees)}`}
            />
            <MetricCard
                testId="metric-company-settlement"
                title="Target transazione consigliato"
                subtitle="Accordo bonario in sede protetta (40–60% del rischio massimo)"
                value={`${eur(r.settlementMinEur)} – ${eur(r.settlementMaxEur)}`}
                accent="border-emerald-500"
                Icon={Scale}
                hint="Fascia ideale per una conciliazione in sede sindacale o ITL, esente da imposte fino a 1 mensilità per anno di servizio."
            />
        </div>
    );
}

function WorkerMetrics({ r }) {
    return (
        <div className="grid grid-cols-1 gap-4">
            <MetricCard
                testId="metric-worker-max"
                title="Stima valore risarcitorio massimo"
                subtitle={`${r.monthsMin.toLocaleString("it-IT")} – ${r.monthsMax.toLocaleString("it-IT")} mensilità × ${eur(r.R)}`}
                value={`${eur(r.riskMinEur)} – ${eur(r.riskMaxEur)}`}
                accent="border-emerald-600"
                Icon={TrendingUp}
                hint={r.reintegraLabel || "Cifra massima teorica monetizzabile in caso di vittoria in Tribunale"}
            />
            <MetricCard
                testId="metric-worker-negotiation"
                title="Valore indicativo trattativa stragiudiziale"
                subtitle="Buonuscita in incentivo all'esodo"
                value={`${eur(r.workerNegotiationMin)} – ${eur(r.workerNegotiationMax)}`}
                accent="border-sky-600"
                Icon={Scale}
                hint="Fascia realistica per chiudere in sede protetta (ITL, sindacale). Esente da imposte fino a 1 mensilità per anno di servizio."
            />
            {r.hasReintegra ? (
                <MetricCard
                    testId="metric-worker-reintegra"
                    title="Opzione tutela: reintegra o indennità sostitutiva"
                    subtitle="Diritto alla scelta (art. 18 c.3 St.Lav. / art. 2 D.Lgs. 23/2015)"
                    value={`${eur(r.substituteAllowanceEur)}`}
                    accent="border-navy"
                    Icon={ShieldCheck}
                    hint="15 mensilità sostitutive della reintegra (oltre al risarcimento). Opzione da esercitare entro 30 giorni dalla sentenza."
                />
            ) : (
                <MetricCard
                    testId="metric-worker-nasp"
                    title="Diritto a NASpI"
                    subtitle="Ammortizzatore sociale disponibile in caso di licenziamento"
                    value="Sì — 24 mesi max"
                    accent="border-navy"
                    Icon={ShieldCheck}
                    hint="Durata pari al 50% delle settimane contribuite ultimi 4 anni. Importo ~75% della retribuzione (con decalage dal 6° mese)."
                />
            )}
        </div>
    );
}
